import {
  viewSessionNotes, SessionNotesView, sessionServiceOptionsDict,
  sessionParticipantOptionsDict, SessionServiceType,
  SessionParticipation, getSessionByAppointmentId,
  getCptCode,
  getDiagnosisByIds,
  AppointmentType,
  PsychiatryNote,
} from '@uplift/api';
import { Session } from '@uplift/types';
import { fetchAppointmentForClinicalNotes } from './use-appointment.hook';
import { useQuery } from '@tanstack/react-query';

export type ClinicalNotesModalStateType = {
  type: AppointmentType;
  isLegacy: boolean;
  appointment: Session;
  session: {
    clinicalNote: string | null;
    id: string;
    cpts: {
      name: string;
      code: string;
      id: string;
    }[];
    diagnosis: {
      name: string;
      code: string;
      id: string;
    }[];
    serviceType?: string;
    participant?: string;
    submittedAt: string;
  };
  sessionNotesView?: SessionNotesView;
  psychiatryNote?: PsychiatryNote;
};

export const useClinicalNotesModal = (appointmentId?: string, enabled = true) => {
  const { data: state } = useQuery(
    [ 'appointment-clinical-notes', appointmentId ] as const,
    async ({ queryKey: [ _, appointmentId ] }): Promise<ClinicalNotesModalStateType | undefined> => {
      if (!appointmentId) {
        return undefined;
      }

      const appointment = await fetchAppointmentForClinicalNotes(appointmentId);
      const session = await fetchSession(appointmentId);

      // if viewSessionNotes throws an error, then it's a legacy session note
      try {
        const sessionNotesView = await viewSessionNotes(session.id);

        if (appointment) {
          if (sessionNotesView.data.type === AppointmentType.therapy) {
            return { type: sessionNotesView.data.type, appointment, session, isLegacy: false, sessionNotesView: sessionNotesView.data.note };
          } else if (sessionNotesView.data.type === AppointmentType.psychiatry) {
            return { type: sessionNotesView.data.type, appointment, session, isLegacy: false, psychiatryNote: sessionNotesView.data.note };
          }
        }

      } catch (error) {
        if (appointment) {
          return { type: appointment.appointmentType as AppointmentType, appointment, session, isLegacy: true, sessionNotesView: undefined, psychiatryNote: undefined };
        }
      }

      return undefined;
    },
    {
      enabled: enabled && !!appointmentId,
    }
  );

  return { state };
};

const fetchSession = async (appointmentId: string): Promise<ClinicalNotesModalStateType['session']> => {
  const { data: session } = await getSessionByAppointmentId(appointmentId);
  const [ diagnosis, ...cptCodes ] = await Promise.all([
    getDiagnosisByIds(session.diagnosisIds),
    ...[ session?.cptCodeId, ...session.addonCptCodeIds ].filter(Boolean).map((c) => getCptCode(c)),
  ]);
  const serviceType = session.serviceType ? sessionServiceOptionsDict[session.serviceType as SessionServiceType] : null;
  const participant = session.participation ? sessionParticipantOptionsDict[session.participation as SessionParticipation] : null;

  return {
    id: session.id,
    cpts: cptCodes.map(cpt => ({
      id: cpt?.data?.id as string,
      name: cpt?.data?.name as string,
      code: cpt?.data?.code as string,
    })),
    clinicalNote: session.clinicalNote ?? null,
    diagnosis: diagnosis.data,
    serviceType: serviceType ?? undefined,
    participant: participant ?? undefined,
    submittedAt: session.submittedAt as string,
  };
};
